import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useChat } from '@ai-sdk/react'
import { DefaultChatTransport, UIMessage } from 'ai'
import { ScrollArea } from '../ui/scroll-area'
import { Button } from '../ui/button'
import { AIChatInput } from './AIChatInput'
import { MessageList } from './MessageList'
import { PromptSuggestions } from './PromptSuggestions'
import { PoweredByWatermark } from './PoweredByWatermark'
import { AIDisclaimer } from './AIDisclaimer'
import { ChevronDown } from 'lucide-react'

import { useRTL } from '../../contexts/RTLContext'
import { useApiClient, useApiClientContext } from '../../contexts/ApiClientContext'
import { useFloating } from '../../contexts/FloatingContext'
import { useInteraction } from '../../contexts/InteractionContext'
import { useSuggestions, useComputedHeight, useRateLimit, useProjectConfig, useContentHeight } from '../../hooks'
import { useMobile } from '../../utils/mobile'
import { chatLogger } from '../../utils/logger'
import { 
  trackInputFocused, 
  trackInputBlurred, 
  trackUserTyping,
  trackSendButtonClicked, 
  trackSuggestionClicked, 
  generateThreadId 
} from '../../services/analytics.service'

import type { ChatRequest } from '@kwry/common'

// Utility function for className merging
function cn(...classes: (string | undefined | null | false)[]): string {
  return classes.filter(Boolean).join(' ')
}

interface ChatContainerProps {
  className?: string
  config?: {
    projectId: string
    position?: 'inline' | 'bottom-right' | 'bottom-left'
    mobileFloatingBar?: boolean
  }
  content?: {
    title?: string
    content?: string
    url?: string
  }
  onResize?: (height: number) => void
  onOpenLink?: (url: string) => void
  onChatStarted?: () => void
  onCenterRequest?: (height: number) => void
  projectIconUrl?: string | null
}
// Rate limit notification component
const RateLimitNotification = ({ rateLimitStatus }: { rateLimitStatus: any }) => {
  const { isRTL } = useRTL()
  
  if (!rateLimitStatus.isRateLimited) return null
  
  const message = rateLimitStatus.getRateLimitMessage()
  
  return (
    <div className="mx-4 mb-2 px-4 py-3 bg-orange-50 border border-orange-200 rounded-lg">
      <div className="flex items-center justify-between">
        <div className={`flex-1 ${isRTL ? 'text-right' : 'text-left'}`}>
          <p className="text-orange-800 text-sm font-medium">{message}</p>
          {rateLimitStatus.rateLimitInfo && (
            <p className="text-orange-600 text-xs mt-1">
              Remaining: {rateLimitStatus.rateLimitInfo.remaining.perMinute} per minute, {rateLimitStatus.rateLimitInfo.remaining.perHour} per hour
            </p>
          )}
        </div>
      </div>
    </div>
  )
}



export function ChatContainer({ className, config, content, onResize, onOpenLink, onChatStarted, onCenterRequest, projectIconUrl }: ChatContainerProps) {
  const [hasBeenCentered, setHasBeenCentered] = useState(false)
  const [currentOptions, setCurrentOptions] = useState<{ think?: boolean; deepSearch?: boolean }>({})
  const [lastMessageLength, setLastMessageLength] = useState(0)
  const [threadId, setThreadId] = useState<string>('')
  const containerRef = useRef<HTMLDivElement>(null)
  const scrollAreaRef = useRef<HTMLDivElement>(null)
  const messageListRef = useRef<HTMLDivElement>(null)
  const baseTimeRef = useRef<number>(Date.now())
  const apiClient = useApiClient()
  const { setThreadId: setApiClientThreadId } = useApiClientContext()
  const { language } = useRTL()
  
  // Get floating state from context
  const { isFloating, floatingConfig } = useFloating()
  
  // Get mobile state
  const isMobile = useMobile()
  
  // Get interaction state from context
  const { 
    hasStartedChat, 
    isInputFocused,
    isChatCollapsed,
    setChatStarted, 
    setInputFocused,
    setChatCollapsed 
  } = useInteraction()
  
  // Get project configuration for ad placement
  const { data: projectConfig } = useProjectConfig()
  
  // Rate limiting awareness
  const rateLimitStatus = useRateLimit()
  const isRateLimited = rateLimitStatus.isRateLimited

  // NEW: Use content height hook for dynamic height management
  // Disable in mobile floating mode (CSS handles height)
  const { 
    height: chatHeight, 
    setTargetElement,
    isUsingDefault 
  } = useContentHeight({
    enabled: hasStartedChat && !isFloating,
    includeInput: true  // We want total height including input
  })

  // Memoize suggestions context to prevent re-renders
  const suggestionsContext = useMemo(() => {
    return content ? {
      articleTitle: content.title,
      articleContent: content.content,
      articleUrl: content.url
    } : undefined
  }, [content?.title, content?.content, content?.url])

  // Get suggestions data to calculate height
  const { 
    data: suggestionsData, 
    isLoading: suggestionsLoading 
  } = useSuggestions({
    context: suggestionsContext,
    language,
    enabled: !!(content?.title || content?.content) && !hasStartedChat
  })

  const suggestions = suggestionsData?.suggestions || []
  const suggestionsCount = suggestionsLoading ? 3 : suggestions.length // Show 3 skeleton items when loading

  // Memoize the config object to prevent re-renders
  const heightConfig = useMemo(() => ({
    hasStartedChat,
    isInputFocused,
    suggestionsCount,
    showSuggestions: !hasStartedChat,
    chatHeight  // Pass the height from useContentHeight
  }), [hasStartedChat, isInputFocused, suggestionsCount, chatHeight])

  // Use simple computed height with hardcoded values
  const computedHeight = useComputedHeight(heightConfig)

  // Memoize content object to prevent re-renders
  const memoizedContent = useMemo(() => {
    return content ? {
      title: content.title,
      content: content.content,
      url: content.url
    } : undefined
  }, [content?.title, content?.content, content?.url])



  // NEW: Set up content measurement when chat starts
  useEffect(() => {
    if (hasStartedChat && messageListRef.current) {
      setTargetElement(messageListRef.current)
    }
  }, [hasStartedChat, setTargetElement])

  // AI SDK v5 requires manual input state management
  const [input, setInput] = useState('')

  const { messages, sendMessage, status, error, stop } = useChat({
    transport: new DefaultChatTransport({
      api: `${apiClient!.getApiBaseUrl()}/chat`,
      headers: () => apiClient!.getHeaders(),
      body: () => ({
        projectId: config?.projectId,
        threadId: threadId || undefined,
        options: currentOptions,
        context: content ? {
          articleTitle: content.title,
          articleContent: content.content,
          articleUrl: content.url
        } : undefined
      } as Partial<ChatRequest>)
    }),
    onFinish: () => {
      setCurrentOptions({}) // Reset options after each message
    }
  })

  // Convert status to isLoading for backward compatibility
  const isLoading = status === 'streaming' || status === 'submitted'

  // Handle scrolling to bottom within the scroll area
  const handleScrollToBottom = useCallback((scrollElement: HTMLElement) => {
    scrollElement.scrollTo({
      top: scrollElement.scrollHeight,
      behavior: 'smooth'
    })
  }, [])

  // Auto-scroll during streaming when content changes
  useEffect(() => {
    // Only auto-scroll during streaming (isLoading = true)
    if (!isLoading || messages.length === 0) return
    
    const lastMessage = messages[messages.length - 1]
    
    // Only auto-scroll for assistant messages being streamed
    if (lastMessage.role !== 'assistant') return
    
    // Check if content actually grew (more responsive than waiting for full updates)
    const currentLength = lastMessage.parts?.reduce((acc, part) => {
      // Only count text parts for length calculation
      if (part.type === 'text' && typeof part.text === 'string') {
        return acc + part.text.length
      }
      return acc
    }, 0) || 0
    
    if (currentLength <= lastMessageLength) return
    setLastMessageLength(currentLength)
    
    // Get the scroll container from ScrollArea
    const scrollContainer = scrollAreaRef.current?.querySelector('[data-radix-scroll-area-viewport]')
    if (scrollContainer) {
      // Check if user hasn't manually scrolled up (within 100px of bottom)
      const isNearBottom = scrollContainer.scrollTop + scrollContainer.clientHeight >= 
        scrollContainer.scrollHeight - 100
      

      
      if (isNearBottom) {
        // Use requestAnimationFrame for smoother scrolling during streaming
        requestAnimationFrame(() => {
          handleScrollToBottom(scrollContainer as HTMLElement)
        })
      }
    }
  }, [messages, isLoading, handleScrollToBottom, lastMessageLength, isFloating])

  // Reset message length tracker when loading stops
  useEffect(() => {
    if (!isLoading) {
      setLastMessageLength(0)
    }
  }, [isLoading])

  // Center iframe in browser viewport when chat starts (only once)
  const centerIframeInViewport = useCallback(() => {
    // Only for inline position (floating widgets don't need this)
    if (config?.position !== 'inline') return
    
    // Only center once per session
    if (hasBeenCentered) return

    // For Shadow DOM implementations, use the callback directly
    if (onCenterRequest) {
      onCenterRequest(computedHeight.height)
    } else {
      // For iframe implementations, send message to parent
      window.parent.postMessage({
        type: 'CENTER_IFRAME',
        data: { height: computedHeight.height }
      }, '*')
    }
    
    // Mark as centered so it doesn't happen again
    setHasBeenCentered(true)
  }, [config?.position, computedHeight.height, onCenterRequest, hasBeenCentered])



  // Resize iframe when computed height changes
  useEffect(() => {
    // Skip resize callbacks in mobile floating mode
    if (isFloating) {
      chatLogger.debug('Mobile floating mode - skipping resize callbacks')
      return
    }
    
    if (!onResize) return
    
    // Resize iframe when height changes
    onResize(computedHeight.height)
    
    // Center the iframe only when chat first starts (not on every height change)
    if (hasStartedChat && !hasBeenCentered) {
      setTimeout(centerIframeInViewport, 100)
    }
  }, [computedHeight.height, onResize, hasStartedChat, hasBeenCentered, centerIframeInViewport, isInputFocused, computedHeight.breakdown.suggestions, computedHeight.isMobile, isFloating])

  const handleSendMessage = async (messageContent: string, options?: { think?: boolean; deepSearch?: boolean }, fromSuggestion?: boolean) => {
    if (!messageContent.trim() || isLoading) return

    // Mark chat as started on first message
    if (!hasStartedChat) {
      chatLogger.debug('First message sent, marking chat as started')
      setChatStarted(true)
      const newThreadId = generateThreadId()
      setThreadId(newThreadId)
      setApiClientThreadId(newThreadId)
      
      // Notify parent component that chat has started
      if (onChatStarted) {
        onChatStarted()
      }
    }

    // Set options for this message
    setCurrentOptions(options || {})

    // Track send button clicked only if NOT from suggestion (suggestion already tracked separately)
    if (!fromSuggestion) {
      trackSendButtonClicked(config?.projectId || 'unknown', threadId, messageContent, messages, options)
    }

    // Use the sendMessage function from useChat (AI SDK v5)
    await sendMessage({
      text: messageContent.trim()
    })
  }

  // Memoize suggestion click handler
  const handleSuggestionClick = useCallback((suggestion: string) => {
    if (!isRateLimited) {
      trackSuggestionClicked(config?.projectId || 'unknown', threadId, suggestion, messages)
      handleSendMessage(suggestion, undefined, true) // fromSuggestion = true
    }
  }, [isRateLimited, handleSendMessage, config?.projectId, threadId, messages])

  // Helper function to extract text content from UIMessage parts
  const extractTextContent = useCallback((parts: any[] = []): string => {
    return parts
      .filter(part => part.type === 'text')
      .map(part => part.text || '')
      .join('')
  }, [])

  // Ref to track last logged ad placement to avoid spam
  const lastLoggedPlacement = useRef<string | null | undefined>(undefined)

  // Helper function to create ad slot message
  const createAdSlot = useCallback((messageId: string, timestamp: Date): any => ({
    id: `ad-after-${messageId}`,
    role: 'ad' as const,
    content: '',
    parts: [],
    timestamp,
    adId: `kwry-ad-after-${messageId}`
  }), [])

  // Memoize the ad placement value to prevent unnecessary re-renders
  const adPlacement = useMemo(() => {
    return projectConfig?.adConfig?.adPlacement
  }, [projectConfig?.adConfig?.adPlacement])

  // Memoize the display messages to prevent excessive re-renders
  const displayMessages = useMemo(() => {
    const adsAlreadyCreated = new Set<string>()
    
    // Only log once when config changes, not on every render
    if (lastLoggedPlacement.current !== adPlacement) {
      lastLoggedPlacement.current = adPlacement
      chatLogger.info('🔍 Ad placement config:', { adPlacement, messagesCount: messages.length })
    }
    
    return messages.reduce((acc: any[], msg, index) => {
      // Add the current message with stable timestamp based on index
      const displayMessage = {
        id: msg.id,
        role: msg.role as 'user' | 'assistant',
        content: extractTextContent(msg.parts),
        parts: msg.parts || [],
        timestamp: new Date(baseTimeRef.current + index * 1000) // Use stable base time
      }
      acc.push(displayMessage)

      // Check if we should add an ad slot after this completed AI message
      if (msg.role === 'assistant' && !isLoading) {
        // Only log when actually creating an ad, not on every check
        if (adPlacement === 'in_chat') {
          const adId = `ad-after-${msg.id}`
          
          // Only create ad if we haven't already created one for this message
          if (!adsAlreadyCreated.has(adId)) {
            adsAlreadyCreated.add(adId)
            const adMessage = createAdSlot(msg.id, new Date(baseTimeRef.current + index * 1000 + 500))
            chatLogger.info('📢 Creating ad slot', { adId, adMessage })
            acc.push(adMessage)
          } else {
            chatLogger.info('⚠️ Ad already created for message', { adId })
          }
        }
        // Note: 'under_chat' placement will be handled in the parent container
        // null placement means no ads
      }

      return acc
    }, [])
  }, [messages, isLoading, extractTextContent, createAdSlot, adPlacement])

  // Conditional rendering without early returns
  return (
    <>
      {!hasStartedChat ? (
        // Default state: input aligned to top with suggestions below
        <div 
          ref={containerRef} 
          className={`flex flex-col ${className}`} 
          style={{ 
            height: isFloating ? 'auto' : `${computedHeight.height}px`,
            // In floating mode, let parent handle constraints
            maxHeight: isFloating ? '100%' : undefined,
            overflow: isFloating ? 'hidden' : undefined,
            // Remove any background in floating mode
            background: isFloating ? 'transparent' : undefined
          }}
        >
          <div className="flex-1 flex items-start justify-center" style={{ background: isFloating ? 'transparent' : undefined }}>
            <div className="w-full max-w-4xl" style={{ background: isFloating ? 'transparent' : undefined }}>
              {/* Render suggestions ABOVE input in mobile floating mode */}
              {isMobile && isFloating && (
                <PromptSuggestions
                  content={memoizedContent}
                  onSuggestionClick={handleSuggestionClick}
                  className="max-w-2xl mx-auto"
                  isFocused={isInputFocused}
                  position="above"
                />
              )}
              
              <AIChatInput 
                onSend={handleSendMessage} 
                disabled={isLoading || isRateLimited} 
                isStreaming={isLoading}
                onStop={stop}
                onFocusChange={setInputFocused}
                projectIconUrl={projectIconUrl}
                onInputFocused={() => trackInputFocused(config?.projectId || 'unknown', threadId, messages)}
                onInputBlurred={() => trackInputBlurred(config?.projectId || 'unknown', threadId, messages)}
                onUserTyping={(inputLength) => trackUserTyping(config?.projectId || 'unknown', threadId, inputLength, messages)}
              />
              <PoweredByWatermark 
                projectId={config?.projectId || 'unknown'} 
                threadId={threadId} 
                messages={messages} 
              />
              <RateLimitNotification rateLimitStatus={rateLimitStatus} />
              
              {/* Render suggestions BELOW input in all other cases */}
              {!(isMobile && isFloating) && (
                <PromptSuggestions
                  content={memoizedContent}
                  onSuggestionClick={handleSuggestionClick}
                  className="max-w-2xl mx-auto"
                  isFocused={isInputFocused}
                  position="below"
                />
              )}
            </div>
          </div>
        </div>
      ) : (
        // Chat started: messages fill space, input at bottom
        <div 
          ref={containerRef}
          className={`flex flex-col transition-all duration-300 ease-in-out ${className}`} 
          style={{ 
            height: isFloating ? '100%' : `${computedHeight.height}px`,
            maxHeight: isFloating ? '100%' : undefined,
            display: 'flex',
            flexDirection: 'column',
            // Remove any background in floating mode
            background: isFloating ? 'transparent' : undefined
          }}
        >
          {/* Collapse button - only show in floating mode when chat is not collapsed */}
          {isFloating && !isChatCollapsed && (
            <div className="flex justify-end p-2" style={{ background: 'transparent' }}>
              <Button
                onClick={() => setChatCollapsed(true)}
                variant="ghost"
                size="sm"
                className="rounded-full p-2 hover:bg-gray-100"
                aria-label="Collapse chat"
              >
                <ChevronDown className="h-5 w-5" />
              </Button>
            </div>
          )}
          
          {/* Only show messages if not collapsed */}
          {!isChatCollapsed && (
            <ScrollArea 
              ref={scrollAreaRef} 
              className="flex-1 px-4 overflow-y-auto min-h-0" 
              style={{ 
                background: isFloating ? 'transparent' : undefined,
                // Ensure proper scrolling on mobile
                WebkitOverflowScrolling: 'touch',
                overscrollBehavior: 'contain'
              }}
            >
              <div ref={messageListRef} style={{ background: isFloating ? 'transparent' : undefined }}> {/* NEW: wrapper with ref for measurement */}
                <MessageList 
                  messages={displayMessages} 
                  isLoading={isLoading} 
                  onScrollToBottom={handleScrollToBottom}
                  projectIconUrl={projectIconUrl}
                  projectId={config?.projectId || 'unknown'}
                  threadId={threadId}
                />
              </div>
              {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg mx-4 mb-4">
                  <p className="text-red-700">Error: {error.message}</p>
                </div>
              )}
            </ScrollArea>
          )}
          
          {/* Only show disclaimer when chat is open - positioned above input */}
          {!isChatCollapsed && <AIDisclaimer />}
          
          <div className="flex-shrink-0" style={{ background: isFloating ? 'transparent' : undefined }}>
            <RateLimitNotification rateLimitStatus={rateLimitStatus} />
            <AIChatInput 
              onSend={handleSendMessage} 
              disabled={isLoading || isRateLimited} 
              isStreaming={isLoading}
              onStop={stop}
              onFocusChange={setInputFocused}
              projectIconUrl={projectIconUrl}
              onInputFocused={() => trackInputFocused(config?.projectId || 'unknown', threadId, messages)}
              onInputBlurred={() => trackInputBlurred(config?.projectId || 'unknown', threadId, messages)}
              onUserTyping={(inputLength) => trackUserTyping(config?.projectId || 'unknown', threadId, inputLength, messages)}
            />
            <PoweredByWatermark 
              projectId={config?.projectId || 'unknown'} 
              threadId={threadId} 
              messages={messages} 
            />
          </div>
        </div>
      )}
    </>
  )
} 